import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuthState } from "@/hooks/useAuthState";
import { useEntitlements } from "@/hooks/useEntitlements";
import { useLanguage } from "@/i18n/LanguageContext";

/**
 * Capa de interfaz de los planes nuevos (Fase 5).
 *
 * Solo está activa si el interruptor entitlements_v2 está encendido, o si un
 * administrador activa la "vista previa" de un plan (solo en su navegador).
 * Con el interruptor apagado y sin vista previa, `active` es false y todos los
 * componentes siguen con la lógica antigua.
 */
export type PlanKey = "lite" | "basico" | "club_pro" | "completo";
export const PLAN_KEYS: PlanKey[] = ["lite", "basico", "club_pro", "completo"];

const PREVIEW_KEY = "rl-v2-preview-plan";
const PREVIEW_EVENT = "rl-v2-preview-plan-change";

const readPreview = (): PlanKey | null => {
  if (typeof window === "undefined") return null;
  const v = window.localStorage.getItem(PREVIEW_KEY);
  return (PLAN_KEYS as string[]).includes(v ?? "") ? (v as PlanKey) : null;
};

export function setPlanPreview(plan: PlanKey | null) {
  if (typeof window === "undefined") return;
  if (plan) window.localStorage.setItem(PREVIEW_KEY, plan);
  else window.localStorage.removeItem(PREVIEW_KEY);
  window.dispatchEvent(new Event(PREVIEW_EVENT));
}

export function usePlanPreview() {
  const [preview, setPreview] = useState<PlanKey | null>(null);
  useEffect(() => {
    setPreview(readPreview());
    const sync = () => setPreview(readPreview());
    window.addEventListener(PREVIEW_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(PREVIEW_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  return preview;
}

export interface PlanRow {
  plan_key: PlanKey; name: string; name_en: string | null; rank: number;
  price_monthly_cents: number; trial_enabled: boolean; trial_days: number; trial_end_behavior?: string | null;
}
export interface CatalogRow {
  catalog_key: string; item_type: string; section: string; name: string; name_en: string | null;
  legacy_module_key: string | null; status: string; sort_order: number;
}
export interface CellRow {
  plan_key: PlanKey; catalog_key: string; value_type: string; limit_value: number | null;
  display_label: string | null; display_label_en: string | null;
}
export interface QuotaRuleRow {
  plan_key: PlanKey; quota_key: string; warning_pct: number; on_reach_action: string;
  target_plan_key: PlanKey | null; allow_downgrade: boolean; packs_enabled: boolean;
  title: string | null; title_en?: string | null; body: string | null; body_en?: string | null;
}
export interface PackRow { id: string; name?: string | null; label?: string | null; label_en?: string | null; tier_order?: number; quota_amount: number | null; price_cents: number | null; active: boolean }

export const planCatalogQueryKey = ["plan-catalog-ui"] as const;

async function loadCatalog() {
  const sb = supabase as any;
  const { data: ver } = await sb.from("catalog_versions").select("id").eq("status", "published")
    .order("published_at", { ascending: false }).limit(1).maybeSingle();
  const vid = ver?.id ?? null;
  const [plans, items, cells, rules, packs] = await Promise.all([
    sb.from("plans").select("plan_key,name,name_en,rank,price_monthly_cents,trial_enabled,trial_days,trial_end_behavior").order("rank"),
    sb.from("catalog_items").select("catalog_key,item_type,section,name,name_en,legacy_module_key,status,sort_order").order("sort_order"),
    vid ? sb.from("plan_items").select("plan_key,catalog_key,value_type,limit_value,display_label,display_label_en").eq("version_id", vid) : { data: [] },
    vid ? sb.from("quota_rules").select("*").eq("version_id", vid) : { data: [] },
    sb.from("quota_pack_tiers").select("*").eq("active", true).order("tier_order"),
  ]);
  return {
    plans: (plans.data ?? []) as PlanRow[],
    items: (items.data ?? []) as CatalogRow[],
    cells: (cells.data ?? []) as CellRow[],
    rules: (rules.data ?? []) as QuotaRuleRow[],
    packs: ((packs.data ?? []) as PackRow[]).filter((p) => p.quota_amount && p.price_cents),
  };
}

const AGENT_QUOTA = "quota.agente_ia_consultas";

/** Valor comparable de una casilla de la matriz (más alto = mejor). */
function cellScore(c: CellRow | undefined): number {
  if (!c) return 0;
  if (c.value_type === "included") return 1_000_000;
  if (c.value_type === "limit") return c.limit_value ?? 0;
  if (c.value_type === "limited") return 0.5;
  return 0;
}

export function usePlanUi() {
  const { user, isReady } = useAuthState();
  const { lang } = useLanguage();
  const es = lang === "es";
  const qc = useQueryClient();
  const preview = usePlanPreview();
  const { entitlements, isLoading: entLoading } = useEntitlements();
  const isAdmin = !!entitlements?.is_admin;
  const previewOn = isAdmin && !!preview;
  const active = !!user && (!!entitlements?.v2_enabled || previewOn);

  const catalogQ = useQuery({ queryKey: planCatalogQueryKey, queryFn: loadCatalog, staleTime: 5 * 60_000, enabled: active });
  const quotaQ = useQuery({
    queryKey: ["agent-quota-ui", user?.id],
    enabled: active && isReady && !!user,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc("agent_quota_status", { _user_id: user!.id });
      if (error) throw error;
      return (Array.isArray(data) ? data[0] : data) as { used: number; limit: number | null; remaining: number | null; credits: number; resets_at: string; plan_key: string } | null;
    },
  });

  const cat = catalogQ.data;
  // Sin plan (lista de acceso anticipado): null, nunca «lite».
  const planKey: PlanKey | null = previewOn ? preview! : ((PLAN_KEYS as string[]).includes(String(entitlements?.plan_key ?? "")) ? (entitlements!.plan_key as PlanKey) : null);
  const plans = cat?.plans ?? [];
  const plan = plans.find((p) => p.plan_key === planKey) ?? null;

  const cell = useCallback((pk: string | null, key: string) => cat?.cells.find((c) => c.plan_key === pk && c.catalog_key === key), [cat]);
  const itemName = useCallback((it: CatalogRow) => (es ? it.name : it.name_en || it.name), [es]);
  const planName = useCallback((pk: string | null | undefined) => {
    if (!pk) return es ? "Sin plan" : "No plan";
    const p = plans.find((x) => x.plan_key === pk);
    return p ? (es ? p.name : p.name_en || p.name) : pk ?? "";
  }, [plans, es]);

  /** ¿Tiene el elemento del catálogo? (vista previa → matriz; real → servidor). */
  const hasItem = useCallback((key: string): boolean => {
    if (!active) return false;
    if (previewOn) { if (!planKey) return false; const c = cell(planKey, key); return !!c && c.value_type !== "not_included" && c.value_type !== "extra"; }
    return !!entitlements?.items?.[key]?.enabled;
  }, [active, previewOn, cell, planKey, entitlements]);

  /** Plan más barato que incluye un elemento. */
  const minPlanFor = useCallback((key: string): PlanRow | null => {
    // Sin plan: no se propone ningún plan (los pagos están en pausa).
    if (!planKey) return null;
    for (const p of plans) {
      if (p.plan_key === "lite") continue;
      if (p.rank <= (plan?.rank ?? 0)) continue;
      const c = cell(p.plan_key, key);
      if (c && c.value_type !== "not_included" && c.value_type !== "extra") return p;
    }
    return null;
  }, [plans, plan, cell, planKey]);

  const label = useCallback((c: CellRow, it: CatalogRow) => {
    const custom = es ? c.display_label : c.display_label_en || c.display_label;
    if (c.value_type === "limit" && c.limit_value != null) return `${itemName(it)}: ${custom || c.limit_value}`;
    if (c.value_type === "limited") return `${itemName(it)}${custom ? ` (${custom})` : es ? " (limitado)" : " (limited)"}`;
    return itemName(it);
  }, [es, itemName]);

  /** Lo que se gana al pasar de `from` a `to` (o lo que se pierde, invirtiendo). */
  const diff = useCallback((from: string | null, to: string | null): string[] => {
    if (!cat) return [];
    const out: string[] = [];
    for (const it of cat.items) {
      if (it.status === "oculto" || it.item_type === "extra") continue;
      // Los módulos internos del método (sabor, semilla…) van dentro de la hoja de ruta: no se listan sueltos.
      if (it.item_type === "roadmap_module" && it.catalog_key !== "roadmap.hoja_de_ruta") continue;
      const a = cell(from, it.catalog_key); const b = cell(to, it.catalog_key);
      if (b && cellScore(b) > cellScore(a)) out.push(label(b, it) + (it.status === "proximamente" ? (es ? " · próximamente" : " · coming soon") : ""));
    }
    return out;
  }, [cat, cell, label, es]);

  const nextPlan = planKey ? (plans.find((p) => p.rank === (plan?.rank ?? 0) + 1 && p.plan_key !== "lite") ?? null) : null;
  const rule = cat?.rules.find((r) => r.plan_key === planKey && r.quota_key === AGENT_QUOTA) ?? null;

  const currentItems = useMemo(() => {
    if (!cat) return [];
    return cat.items
      .filter((it) => it.status !== "oculto")
      .map((it) => {
        const matrixCell = cat.cells.find((c) => c.plan_key === planKey && c.catalog_key === it.catalog_key);
        const effective = previewOn ? matrixCell : entitlements?.items?.[it.catalog_key];
        return {
          ...it,
          value_type: effective?.value_type ?? matrixCell?.value_type ?? "not_included",
          limit: effective && "limit" in effective ? effective.limit : matrixCell?.limit_value ?? null,
          enabled: effective && "enabled" in effective ? effective.enabled : (!!matrixCell && matrixCell.value_type !== "not_included" && matrixCell.value_type !== "extra"),
        };
      });
  }, [cat, entitlements, planKey, previewOn]);

  const q = quotaQ.data;
  const quotaCell = cell(planKey, AGENT_QUOTA);
  const quotaLimit = !planKey ? 0 : previewOn ? (quotaCell?.value_type === "included" ? null : quotaCell?.limit_value ?? 0) : (q?.limit ?? null);
  const used = q?.used ?? 0;
  const credits = q?.credits ?? 0;
  const remaining = quotaLimit == null ? null : Math.max(quotaLimit - used, 0) + credits;
  const pct = quotaLimit ? Math.min(used / quotaLimit, 1) * 100 : 0;
  const exhausted = quotaLimit != null && remaining === 0;
  const warning = !exhausted && quotaLimit != null && pct >= (rule?.warning_pct ?? 80);

  const refresh = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ["entitlements"] });
    void qc.invalidateQueries({ queryKey: ["agent-quota-ui"] });
    void qc.invalidateQueries({ queryKey: ["plan-subscription-ui"] });
  }, [qc]);

  return useMemo(() => ({
    active, loading: entLoading || (active && catalogQ.isLoading), previewOn, isAdmin, es,
    planKey, plan, plans, nextPlan, planName, hasItem, minPlanFor, diff, rule,
    packs: cat?.packs ?? [], items: cat?.items ?? [], currentItems, legacySource: entitlements?.plan_source ?? null,
    quota: { used, limit: quotaLimit, remaining, credits, pct, exhausted, warning, resetsAt: q?.resets_at ?? null },
    catalogKeyForModule: (moduleKey: string) => cat?.items.find((i) => i.legacy_module_key === moduleKey)?.catalog_key ?? null,
    refresh,
  }), [active, entLoading, catalogQ.isLoading, previewOn, isAdmin, es, planKey, plan, plans, nextPlan, planName, hasItem, minPlanFor, diff, rule, cat, currentItems, entitlements, used, quotaLimit, remaining, credits, pct, exhausted, warning, q, refresh]);
}

/** Puerta de una herramienta: con la capa nueva activa manda la matriz; si no, la lógica antigua. */
export function usePlanGate(catalogKey: string, legacyAllowed: boolean) {
  const ui = usePlanUi();
  if (!ui.active) return { allowed: legacyAllowed, active: false, upgradeTo: null as PlanRow | null, ui };
  const allowed = ui.hasItem(catalogKey);
  return { allowed, active: true, upgradeTo: allowed ? null : ui.minPlanFor(catalogKey), ui };
}
